import { BookOpen, Check, ChevronLeft, Eye, Layers, Loader2, PenTool, Save, Sparkles, Star, Stethoscope, Target, Upload, Wand2 } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import UploadFlowSteps from "../UploadFlowSteps";

const UploadMode = ({
  // Upload state
  uploadStep, setUploadStep,
  uploadedStoryBible, setUploadedStoryBible,
  uploadedChaptersVerbatim, setUploadedChaptersVerbatim,
  uploadedCharacterProfiles, setUploadedCharacterProfiles,
  uploadedWorldRules, setUploadedWorldRules,
  uploadedCategories, setUploadedCategories,
  uploadConflicts, setUploadConflicts,
  conflictResolutions, setConflictResolutions,
  resolvingConflicts, setResolvingConflicts,
  // Analysis state
  analyzingFile, setAnalyzingFile,
  manuscriptId,
  manuscriptAnalysis, setManuscriptAnalysis,
  manuscriptDiagnosis, setManuscriptDiagnosis,
  manuscriptOutlines, setManuscriptOutlines,
  uploadActivityLog, setUploadActivityLog,
  // Improvement state
  selectedImprovements, setSelectedImprovements,
  fixingIssue, setFixingIssue,
  fixedIssues, setFixedIssues,
  appliedIdeas, setAppliedIdeas,
  applyingIdea, setApplyingIdea,
  previewingIdea, setPreviewingIdea,
  previewChanges, setPreviewChanges,
  uploadingStoryBible, setUploadingStoryBible,
  uploadingOutline, setUploadingOutline,
  // Form state
  formData, setFormData,
  planReviewOutline, setPlanReviewOutline,
  chapterPlanMode, setChapterPlanMode,
  chapterSize, setChapterSize,
  complexityLevel, setComplexityLevel,
  // Actions
  setMode, setStep, setLoading,
  loading,
  onCreated,
  onOpenChange,
  // Utilities
  API, userId, navigate,
  pollManuscriptOutlines, playComplete,
  populateFormFromUploads,
}) => {
  return (
          <div className="space-y-4">
            {/* NEW: Multi-step upload flow */}
            {uploadStep === "upload" && !uploadedStoryBible && (
              <UploadFlowSteps
                onComplete={(data) => {
                  // Create story bible from uploaded data
                  // CRITICAL FIX: Build raw_chapter_texts dictionary for verbatim generation
                  // The AI generation engine reads from raw_chapter_texts to get the author's
                  // exact outline, NOT from chapter_outlines.summary (which may be truncated)
                  const rawChapterTexts = {};
                  (data.chapters || []).forEach((ch, i) => {
                    const chNum = ch.number || i + 1;
                    const rawText = ch.summary || ch.content || '';
                    if (rawText && rawText.length > 0) {
                      rawChapterTexts[String(chNum)] = rawText;
                    }
                  });
                  
                  const storyBible = {
                    title: '',
                    genre: '',
                    themes: [],
                    characters: data.characters || [],
                    chapter_outlines: (data.chapters || []).map((ch, i) => ({
                      number: ch.number || i + 1,
                      title: ch.title || `Chapter ${ch.number || i + 1}`,
                      summary: ch.summary || ch.content || '', // VERBATIM content
                      key_beats: ch.key_beats || [],
                      pov: ch.pov || '',
                      setting: ch.setting || '',
                      extraction_method: 'verbatim'
                    })),
                    // CRITICAL: Store raw chapter texts for verbatim generation
                    // Format: { "1": "full outline text...", "2": "full outline text...", ... }
                    raw_chapter_texts: rawChapterTexts,
                    world_building: data.world || {},
                    elevator_pitch: ''
                  };
                  
                  console.log(`[VERBATIM] Created story bible with ${Object.keys(rawChapterTexts).length} raw chapter texts`);
                  
                  setUploadedStoryBible(storyBible);
                  
                  // Also set planReviewOutline for the chapter planning step
                  if (data.chapters && data.chapters.length > 0) {
                    const chaptersForReview = data.chapters.map((ch, i) => ({
                      number: ch.number || i + 1,
                      title: ch.title || `Chapter ${ch.number || i + 1}`,
                      summary: ch.summary || ch.content || '',
                      key_beats: ch.key_beats || [],
                      pov: ch.pov || '',
                      setting: ch.setting || ''
                    }));
                    setPlanReviewOutline(chaptersForReview);
                  }
                  
                  // Auto-populate form fields from uploads
                  const formUpdates = {};
                  
                  // Number of chapters
                  if (data.chapters && data.chapters.length > 0) {
                    formUpdates.num_chapters = data.chapters.length;
                  }
                  
                  // Beginning/Middle/End from auto-calculated story structure
                  if (data.storyStructure) {
                    if (data.storyStructure.beginning) {
                      formUpdates.premise_beginning = data.storyStructure.beginning;
                    }
                    if (data.storyStructure.middle) {
                      formUpdates.premise_middle = data.storyStructure.middle;
                    }
                    if (data.storyStructure.ending) {
                      formUpdates.premise_ending = data.storyStructure.ending;
                    }
                  }
                  
                  // World-building fields
                  if (data.world) {
                    if (data.world.world_type) formUpdates.world_type = data.world.world_type;
                    if (data.world.era) formUpdates.era = data.world.era;
                    if (data.world.world_name) formUpdates.world_name = data.world.world_name;
                    if (data.world.tech_level) formUpdates.tech_level = data.world.tech_level;
                  }
                  
                  // Apply all form updates
                  if (Object.keys(formUpdates).length > 0) {
                    setFormData(prev => ({ ...prev, ...formUpdates }));
                  }
                  
                  // Update categories
                  setUploadedCategories({
                    chapters: { status: 'success', count: (data.chapters || []).length, data: data.chapters || [] },
                    characters: { status: 'success', count: (data.characters || []).length, data: data.characters || [] },
                    world: { status: 'success', data: data.world || {} }
                  });
                  
                  toast.success(`Upload complete! ${(data.chapters || []).length} chapters ready.`);
                }}
                onBack={() => setMode(null)}
                setUploadedChapters={setUploadedChaptersVerbatim}
                setUploadedCharacters={setUploadedCharacterProfiles}
                setUploadedWorld={setUploadedWorldRules}
                uploadedChapters={uploadedChaptersVerbatim}
                uploadedCharacters={uploadedCharacterProfiles}
                uploadedWorld={uploadedWorldRules}
              />
            )}
            {uploadedStoryBible && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-foreground text-lg flex items-center gap-2">
                      <Check className="w-5 h-5 text-green-500" />
                      Story Bible Loaded!
                    </h3>
                    <p className="text-sm text-muted-foreground">Review what we extracted from your files</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => { setUploadedStoryBible(null); setUploadConflicts([]); }}>
                    Upload Different Files
                  </Button>
                </div>
                
                {/* Stats */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-400">{uploadedStoryBible.characters?.length || 0}</p>
                    <p className="text-xs text-muted-foreground">Characters</p>
                  </div>
                  <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-blue-400">{uploadedStoryBible.chapter_outlines?.length || 0}</p>
                    <p className="text-xs text-muted-foreground">Chapters</p>
                  </div>
                </div>
                
                {/* Title & Genre */}
                {(uploadedStoryBible.title || uploadedStoryBible.genre) && (
                  <div className="bg-slate-800/50 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-1">Story</p>
                    <p className="font-medium text-foreground">{uploadedStoryBible.title || 'Untitled'}</p>
                    {uploadedStoryBible.genre && <Badge variant="outline" className="mt-1">{uploadedStoryBible.genre}</Badge>}
                  </div>
                )}
                
                {/* Elevator Pitch */}
                {uploadedStoryBible.elevator_pitch && (
                  <div className="bg-slate-800/50 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-1">Synopsis</p>
                    <p className="text-sm text-foreground">{uploadedStoryBible.elevator_pitch.slice(0, 200)}{uploadedStoryBible.elevator_pitch.length > 200 ? '...' : ''}</p>
                  </div>
                )}
                
                {/* Characters Preview */}
                {uploadedStoryBible.characters?.length > 0 && (
                  <div className="bg-slate-800/50 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-2">Characters ({uploadedStoryBible.characters.length})</p>
                    <div className="flex flex-wrap gap-2">
                      {uploadedStoryBible.characters.slice(0, 8).map((c, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">
                          {c.name} <span className="text-muted-foreground ml-1">({c.role})</span>
                        </Badge>
                      ))}
                      {uploadedStoryBible.characters.length > 8 && (
                        <Badge variant="outline" className="text-xs">+{uploadedStoryBible.characters.length - 8} more</Badge>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Chapters Preview */}
                {uploadedStoryBible.chapter_outlines?.length > 0 && (
                  <div className="bg-slate-800/50 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-2">Chapter Outlines ({uploadedStoryBible.chapter_outlines.length})</p>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {uploadedStoryBible.chapter_outlines.slice(0, 5).map((ch, i) => (
                        <p key={i} className="text-xs text-foreground">
                          <span className="text-cyan-400 font-medium">Ch {ch?.number || i+1}:</span> {ch?.title || ch?.summary?.slice(0, 50) || 'Untitled'}
                        </p>
                      ))}
                      {uploadedStoryBible.chapter_outlines.length > 5 && (
                        <p className="text-xs text-muted-foreground">...and {uploadedStoryBible.chapter_outlines.length - 5} more chapters</p>
                      )}
                    </div>
                  </div>
                )}
                
                {/* CONFLICT RESOLUTION UI */}
                {uploadConflicts.length > 0 && (
                  <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4 space-y-3" data-testid="conflict-resolution-section">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-red-600 flex items-center justify-center">
                        <span className="text-white text-xs font-bold">!</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-red-300">Conflicts Detected</p>
                        <p className="text-xs text-muted-foreground">We found inconsistent information. Please choose which version is correct:</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {uploadConflicts.map((conflict, idx) => (
                        <div key={conflict.conflict_id || idx} className="bg-slate-800/50 rounded-lg p-3">
                          <p className="text-sm text-foreground mb-2">
                            <Badge variant="outline" className="mr-2 text-xs">{conflict.type}</Badge>
                            {conflict.description}
                          </p>
                          <div className="space-y-2">
                            {conflict.options?.map((opt) => (
                              <label 
                                key={opt.key} 
                                className={`flex items-start gap-2 p-2 rounded cursor-pointer transition-colors ${
                                  conflictResolutions[conflict.conflict_id] === opt.key 
                                    ? opt.key === 'ignore' 
                                      ? 'bg-blue-900/30 border border-blue-500/50'
                                      : 'bg-green-900/30 border border-green-500/50' 
                                    : opt.key === 'ignore'
                                      ? 'bg-slate-800/30 border border-slate-600/30 hover:bg-slate-700/50'
                                      : 'hover:bg-slate-700/50'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name={conflict.conflict_id}
                                  value={opt.key}
                                  checked={conflictResolutions[conflict.conflict_id] === opt.key}
                                  onChange={() => setConflictResolutions(prev => ({
                                    ...prev,
                                    [conflict.conflict_id]: opt.key
                                  }))}
                                  className="mt-1"
                                />
                                <div className="flex-1">
                                  <p className={`text-sm font-medium ${opt.key === 'ignore' ? 'text-blue-300' : 'text-foreground'}`}>
                                    {opt.key === 'ignore' ? '⊘ ' : `${opt.key.toUpperCase()}. `}{opt.value}
                                  </p>
                                  {opt.source && opt.key !== 'ignore' && <p className="text-xs text-muted-foreground">Source: {opt.source}</p>}
                                </div>
                              </label>
                            ))}
                            {/* Always show ignore option if not present from backend */}
                            {!conflict.options?.some(o => o.key === 'ignore') && (
                              <label 
                                className={`flex items-start gap-2 p-2 rounded cursor-pointer transition-colors ${
                                  conflictResolutions[conflict.conflict_id] === 'ignore' 
                                    ? 'bg-blue-900/30 border border-blue-500/50'
                                    : 'bg-slate-800/30 border border-slate-600/30 hover:bg-slate-700/50'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name={conflict.conflict_id}
                                  value="ignore"
                                  checked={conflictResolutions[conflict.conflict_id] === 'ignore'}
                                  onChange={() => setConflictResolutions(prev => ({
                                    ...prev,
                                    [conflict.conflict_id]: 'ignore'
                                  }))}
                                  className="mt-1"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-blue-300">⊘ Ignore - Keep both (not a real conflict)</p>
                                </div>
                              </label>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <Button
                      onClick={async () => {
                        const unresolvedCount = uploadConflicts.filter(c => !conflictResolutions[c.conflict_id]).length;
                        if (unresolvedCount > 0) {
                          toast.error(`Please resolve all ${unresolvedCount} remaining conflicts`);
                          return;
                        }
                        
                        setResolvingConflicts(true);
                        try {
                          toast.loading("Applying your choices...", { id: "resolve-conflicts" });
                          const res = await axios.post(`${API}/resolve-conflicts`, {
                            story_bible: uploadedStoryBible,
                            conflicts: uploadConflicts,
                            resolutions: Object.entries(conflictResolutions).map(([id, key]) => ({
                              conflict_id: id,
                              chosen_option: key
                            }))
                          });
                          toast.dismiss("resolve-conflicts");
                          
                          if (res.data.success) {
                            setUploadedStoryBible(res.data.story_bible);
                            setUploadConflicts([]);
                            setConflictResolutions({});
                            toast.success("Conflicts resolved!");
                          }
                        } catch (err) {
                          toast.dismiss("resolve-conflicts");
                          toast.error(err.response?.data?.detail || "Failed to resolve conflicts");
                        } finally {
                          setResolvingConflicts(false);
                        }
                      }}
                      disabled={resolvingConflicts || Object.keys(conflictResolutions).length < uploadConflicts.length}
                      className="w-full bg-green-600 hover:bg-green-700"
                      data-testid="apply-conflict-choices-btn"
                    >
                      {resolvingConflicts ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Applying...
                        </>
                      ) : (
                        <>
                          <Check className="w-4 h-4 mr-2" />
                          Apply My Choices ({Object.keys(conflictResolutions).length}/{uploadConflicts.length})
                        </>
                      )}
                    </Button>
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="flex flex-col gap-2 pt-2">
                  {/* Primary Continue Button - hidden when conflicts need resolution */}
                  {uploadConflicts.length === 0 && (
                  <Button
                    onClick={() => {
                      const sb = uploadedStoryBible;
                      const hasTitle = sb?.title || formData.title;
                      const hasGenre = sb?.genre || formData.genre;
                      const hasChapters = planReviewOutline?.length > 0;
                      
                      // Always populate form from uploads before transitioning
                      if (populateFormFromUploads) {
                        populateFormFromUploads();
                      }
                      
                      if (!hasTitle || !hasGenre) {
                        setComplexityLevel("detailed");
                        setMode("manual");
                        setStep(1);
                        toast.info("Let's fill in the title and genre before creating your book.");
                      } else if (hasChapters) {
                        setComplexityLevel("detailed");
                        setMode("manual");
                        setStep(7);
                        setChapterPlanMode('upload');
                      } else {
                        setComplexityLevel("detailed");
                        setMode("manual");
                        setStep(7);
                        toast.info("No chapter outlines found. You can create them in the next step.");
                      }
                    }}
                    className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                    data-testid="upload-continue-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Continue to Create Book
                  </Button>
                  )}
                  
                  {/* Save Progress Button - allows skipping conflict resolution */}
                  {uploadConflicts.length > 0 && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        const sb = uploadedStoryBible;
                        const hasTitle = sb?.title || formData.title;
                        const hasGenre = sb?.genre || formData.genre;
                        
                        // Mark conflicts as skipped (user chose to keep all values)
                        const skippedResolutions = {};
                        uploadConflicts.forEach(c => {
                          skippedResolutions[c.conflict_id] = 'ignore';
                        });
                        
                        // Update story bible to mark conflicts as skipped
                        setUploadedStoryBible(prev => ({
                          ...prev,
                          conflicts_skipped: true,
                          skipped_conflicts_count: uploadConflicts.length
                        }));
                        
                        // Clear conflicts
                        setUploadConflicts([]);
                        setConflictResolutions({});
                        
                        toast.success(`Saved! ${uploadConflicts.length} conflict(s) kept as-is.`);
                        
                        if (!hasTitle || !hasGenre) {
                          setComplexityLevel("detailed");
                          setMode("manual");
                          setStep(1);
                          toast.info("Let's fill in the title and genre before creating your book.");
                        } else {
                          setComplexityLevel("detailed");
                          setMode("manual");
                          setStep(7);
                        }
                      }}
                      className="flex-1 border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
                      data-testid="upload-save-progress-btn"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save Progress (Skip Conflicts)
                    </Button>
                  )}
                </div>
                
                <p className="text-xs text-center text-muted-foreground">
                  You can edit any details in the next steps before generating
                </p>
                
                <Button variant="outline" onClick={() => { setUploadedStoryBible(null); setUploadConflicts([]); setMode("choose"); }} className="w-full">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Back to Options
                </Button>
              </div>
            )}

            {/* Legacy: Old manuscript upload steps (keep for analyze flow) */}
            {uploadStep !== "upload" && !uploadedStoryBible && (
              <div className="space-y-4">
                <div className="text-center mb-2">
                  <Check className="w-8 h-8 mx-auto text-emerald-400 mb-2" />
                  <h3 className="font-semibold text-lg text-foreground">File Uploaded!</h3>
                  <p className="text-sm text-muted-foreground">What would you like to do with your manuscript?</p>
                </div>
                
                <div className="grid gap-3">
                  {/* Analyze */}
                  <Card 
                    className="cursor-pointer border-2 hover:border-cyan-400 transition-all bg-card"
                    onClick={async () => {
                      setUploadStep("analyzing");
                      setAnalyzingFile(true);
                      const logEntry = (msg) => {
                        // Ensure msg is a simple string to avoid serialization issues
                        const safeMsg = typeof msg === 'string' ? msg : String(msg);
                        setUploadActivityLog(prev => [...prev, { time: new Date().toLocaleTimeString(), msg: safeMsg }]);
                      };
                      logEntry("Starting AI analysis of your manuscript...");
                      try {
                        const res = await axios.post(`${API}/manuscript/analyze/${manuscriptId}`, {}, { timeout: 120000 });
                        logEntry(`Analysis complete: "${res.data.analysis?.title}" — ${res.data.analysis?.chapters_detected} chapters detected`);
                        setManuscriptAnalysis(res.data.analysis);
                        setUploadStep("analysis_done");
                        toast.success("Analysis complete!");
                      } catch (err) {
                        const detail = err?.response?.data?.detail || err?.message || "Analysis failed";
                        logEntry(`ERROR: ${detail}`);
                        toast.error(detail);
                        setUploadStep("results");
                      } finally {
                        setAnalyzingFile(false);
                      }
                    }}
                    data-testid="analyze-file-btn"
                  >
                    <CardContent className="p-4 flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center shrink-0">
                        <Eye className="w-5 h-5 text-cyan-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground">Analyze File</h3>
                        <p className="text-sm text-muted-foreground">Discover what your story is about, chapter count, completeness, and structure</p>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Write My Book */}
                  <Card 
                    className="cursor-pointer border-2 hover:border-cyan-500/35 transition-all bg-card"
                    onClick={async () => {
                      setUploadStep("analyzing");
                      setAnalyzingFile(true);
                      const logEntry = (msg) => {
                        // Ensure msg is a simple string to avoid serialization issues
                        const safeMsg = typeof msg === 'string' ? msg : String(msg);
                        setUploadActivityLog(prev => [...prev, { time: new Date().toLocaleTimeString(), msg: safeMsg }]);
                      };
                      try {
                        // Analyze first if not done
                        if (!manuscriptAnalysis) {
                          logEntry("Step 1/2: Analyzing manuscript structure...");
                          const aRes = await axios.post(`${API}/manuscript/analyze/${manuscriptId}`, {}, { timeout: 120000 });
                          setManuscriptAnalysis(aRes.data.analysis);
                          logEntry(`Analysis done: "${aRes.data.analysis?.title}" — ${aRes.data.analysis?.chapters_detected} chapters`);
                        }
                        // Then generate outlines via background job
                        logEntry("Step 2/2: Generating chapter outlines (this may take a minute)...");
                        const result = await pollManuscriptOutlines(manuscriptId, userId, (status) => {
                          if (status.status === "PROCESSING") {
                            logEntry(`Outline batch ${status.current_batch}/${status.total_batches} processing...`);
                          }
                        });
                        logEntry(`Outlines complete: ${result.chapter_count} chapters generated`);
                        setManuscriptOutlines(result.outlines);
                        setUploadStep("outlines");
                        toast.success(`${result.chapter_count} chapter outlines created!`);
                      } catch (err) {
                        const detail = err?.response?.data?.detail || err?.message || "Generation failed";
                        logEntry(`ERROR: ${detail}`);
                        toast.error(detail);
                        setUploadStep("results");
                      } finally {
                        setAnalyzingFile(false);
                      }
                    }}
                    data-testid="write-book-btn"
                  >
                    <CardContent className="p-4 flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center shrink-0">
                        <PenTool className="w-5 h-5 text-cyan-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground">Write My Book</h3>
                        <p className="text-sm text-muted-foreground">AI analyzes your file, creates outlines, and generates the full book</p>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Is It Good? */}
                  <Card 
                    className="cursor-pointer border-2 hover:border-purple-400 transition-all bg-card"
                    onClick={async () => {
                      setUploadStep("analyzing");
                      setAnalyzingFile(true);
                      const logEntry = (msg) => {
                        // Ensure msg is a simple string to avoid serialization issues
                        const safeMsg = typeof msg === 'string' ? msg : String(msg);
                        setUploadActivityLog(prev => [...prev, { time: new Date().toLocaleTimeString(), msg: safeMsg }]);
                      };
                      try {
                        if (!manuscriptAnalysis) {
                          logEntry("Step 1/2: Analyzing manuscript structure...");
                          const aRes = await axios.post(`${API}/manuscript/analyze/${manuscriptId}`, {}, { timeout: 120000 });
                          setManuscriptAnalysis(aRes.data.analysis);
                          logEntry(`Analysis done: "${aRes.data.analysis?.title}"`);
                        }
                        logEntry("Step 2/2: Running AI diagnosis...");
                        const dRes = await axios.post(`${API}/manuscript/diagnose/${manuscriptId}`, {}, { timeout: 120000 });
                        setManuscriptDiagnosis(dRes.data.diagnosis);
                        logEntry(`Diagnosis complete: Score ${dRes.data.diagnosis?.overall_score}/100`);
                        setUploadStep("diagnose");
                        toast.success("Diagnosis complete!");
                      } catch (err) {
                        const detail = err?.response?.data?.detail || err?.message || "Diagnosis failed";
                        logEntry(`ERROR: ${detail}`);
                        toast.error(detail);
                        setUploadStep("results");
                      } finally {
                        setAnalyzingFile(false);
                      }
                    }}
                    data-testid="is-it-good-btn"
                  >
                    <CardContent className="p-4 flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center shrink-0">
                        <Stethoscope className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground">Is It Good?</h3>
                        <p className="text-sm text-muted-foreground">AI diagnoses your story, scores it, and suggests specific improvements</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Activity Log */}
                {uploadActivityLog.length > 0 && (
                  <div className="bg-slate-900/80 border border-slate-700 rounded-lg p-3 max-h-32 overflow-y-auto" data-testid="results-activity-log">
                    <p className="text-xs font-mono text-slate-400 mb-1">Activity Log</p>
                    {uploadActivityLog.map((entry, i) => (
                      <p key={i} className={`text-xs font-mono ${entry.msg.startsWith('ERROR') ? 'text-red-400' : 'text-slate-400'}`}>
                        <span className="text-slate-500">[{entry.time}]</span> {entry.msg}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Analyzing spinner */}
            {uploadStep === "analyzing" && (
              <div className="text-center py-8 space-y-4">
                <div className="w-12 h-12 border-3 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto" />
                <h3 className="font-semibold text-foreground">AI is reading your manuscript...</h3>
                <p className="text-sm text-muted-foreground">This may take 30-60 seconds for longer documents</p>

                {/* Activity Log during processing */}
                {uploadActivityLog.length > 0 && (
                  <div className="mt-4 bg-slate-900/80 border border-slate-700 rounded-lg p-3 max-h-40 overflow-y-auto text-left mx-auto max-w-md" data-testid="analyzing-activity-log">
                    <p className="text-xs font-mono text-slate-400 mb-1">Activity Log</p>
                    {uploadActivityLog.map((entry, i) => (
                      <p key={i} className={`text-xs font-mono ${entry.msg.startsWith('ERROR') ? 'text-red-400' : 'text-slate-400'}`}>
                        <span className="text-slate-500">[{entry.time}]</span> {entry.msg}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Analysis Results */}
            {uploadStep === "analysis_done" && manuscriptAnalysis && (
              <div className="space-y-4">
                <div className="text-center mb-2">
                  <h3 className="font-semibold text-lg text-foreground">{manuscriptAnalysis.title || "Your Manuscript"}</h3>
                  <p className="text-sm text-muted-foreground">{manuscriptAnalysis.genre} — {manuscriptAnalysis.chapters_detected} chapters detected</p>
                </div>
                
                {/* Summary */}
                <div className="bg-card border border-border rounded-lg p-4">
                  <p className="text-sm text-foreground">{manuscriptAnalysis.summary}</p>
                </div>
                
                {/* Stats Grid */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-card border border-border rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-cyan-400">{manuscriptAnalysis.chapters_detected || 0}</div>
                    <div className="text-xs text-muted-foreground">Chapters Found</div>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-cyan-400">{manuscriptAnalysis.suggested_chapter_count || manuscriptAnalysis.chapters_detected}</div>
                    <div className="text-xs text-muted-foreground">Suggested</div>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-emerald-400">{manuscriptAnalysis.completeness_percent || 0}%</div>
                    <div className="text-xs text-muted-foreground">Complete</div>
                  </div>
                </div>
                
                {/* Merge suggestions with Fix button */}
                {manuscriptAnalysis.chapter_merge_suggestions?.length > 0 && (
                  <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-3">
                    <h4 className="text-sm font-semibold text-cyan-400 mb-2 flex items-center gap-2">
                      <Layers className="w-4 h-4" />
                      Chapter Merge Suggestions
                    </h4>
                    {manuscriptAnalysis.chapter_merge_suggestions.map((m, i) => {
                      const isApplied = m.applied || fixedIssues.includes(`merge-${i}`);
                      const isQueued = m.queued || fixedIssues.includes(`merge-queued-${i}`);
                      return (
                        <div key={i} className={`flex items-start justify-between gap-2 mb-2 p-2 rounded ${isApplied ? 'bg-emerald-500/10 border border-emerald-500/30' : isQueued ? 'bg-cyan-500/10 border border-cyan-500/30' : 'bg-slate-800/50'}`}>
                          <div className="flex-1">
                            <p className={`text-xs ${isApplied ? 'text-emerald-400 line-through' : isQueued ? 'text-cyan-400' : 'text-muted-foreground'}`}>
                              {isApplied ? '✓ ' : isQueued ? '⏳ ' : ''}Merge chapters {m.merge?.join(" & ")} — {m.reason}
                            </p>
                          </div>
                          {!isApplied && !isQueued && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className={`h-6 px-2 text-xs ${manuscriptOutlines ? 'bg-cyan-600/20 hover:bg-cyan-600/40 text-cyan-400' : 'bg-cyan-600/20 hover:bg-cyan-600/40 text-cyan-400'}`}
                              disabled={fixingIssue !== null}
                              onClick={async () => {
                                setFixingIssue({ type: 'merge', index: i, description: m.reason });
                                try {
                                  const res = await axios.post(`${API}/manuscript/fix-issue/${manuscriptId}`, {
                                    issue_type: 'merge',
                                    issue_description: m.reason,
                                    issue_index: i,
                                    chapters_to_merge: m.merge || []
                                  }, { timeout: 120000 });
                                  if (res.data.success) {
                                    if (res.data.pending) {
                                      setFixedIssues(prev => [...prev, `merge-queued-${i}`]);
                                      toast.success(res.data.message || "Merge queued! Will apply when outlines are created.");
                                    } else {
                                      setFixedIssues(prev => [...prev, `merge-${i}`]);
                                      // Refresh outlines
                                      const outlineRes = await axios.get(`${API}/manuscript/outlines-status/${manuscriptId}`);
                                      if (outlineRes.data?.outlines) {
                                        setManuscriptOutlines(outlineRes.data.outlines);
                                      }
                                      toast.success(res.data.message || "Chapters merged successfully!");
                                    }
                                    playComplete();
                                  } else {
                                    toast.error(res.data.message || "Failed to merge chapters");
                                  }
                                } catch (err) {
                                  toast.error(err?.response?.data?.detail || "Failed to fix issue");
                                } finally {
                                  setFixingIssue(null);
                                }
                              }}
                              data-testid={`fix-merge-${i}`}
                            >
                              {fixingIssue?.type === 'merge' && fixingIssue?.index === i ? (
                                <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> {manuscriptOutlines ? 'Merging...' : 'Queuing...'}</>
                              ) : (
                                <><Wand2 className="w-3 h-3 mr-1" /> {manuscriptOutlines ? 'Merge Now' : 'Queue Merge'}</>
                              )}
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* Gaps with Fix button */}
                {manuscriptAnalysis.gaps?.length > 0 && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                    <h4 className="text-sm font-semibold text-red-400 mb-2 flex items-center gap-2">
                      <Target className="w-4 h-4" />
                      Gaps Found
                    </h4>
                    {manuscriptAnalysis.gaps.map((g, i) => {
                      const isFixed = g.startsWith('✓ FIXED:') || fixedIssues.includes(`gap-${i}`);
                      const isQueued = g.startsWith('⏳ QUEUED:') || fixedIssues.includes(`gap-queued-${i}`);
                      return (
                        <div key={i} className={`flex items-start justify-between gap-2 mb-2 p-2 rounded ${isFixed ? 'bg-emerald-500/10 border border-emerald-500/30' : isQueued ? 'bg-cyan-500/10 border border-cyan-500/20' : 'bg-slate-800/50'}`}>
                          <p className={`text-xs flex-1 ${isFixed ? 'text-emerald-400' : isQueued ? 'text-cyan-400' : 'text-muted-foreground'}`}>
                            • {g}
                          </p>
                          {!isFixed && !isQueued && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className={`h-6 px-2 text-xs ${manuscriptOutlines ? 'bg-red-600/20 hover:bg-red-600/40 text-red-400' : 'bg-cyan-600/20 hover:bg-cyan-600/40 text-cyan-400'}`}
                              disabled={fixingIssue !== null}
                              onClick={async () => {
                                setFixingIssue({ type: 'gap', index: i, description: g });
                                try {
                                  const res = await axios.post(`${API}/manuscript/fix-issue/${manuscriptId}`, {
                                    issue_type: 'gap',
                                    issue_description: g,
                                    issue_index: i,
                                    chapters_to_merge: []
                                  }, { timeout: 120000 });
                                  if (res.data.success) {
                                    if (res.data.pending) {
                                      setFixedIssues(prev => [...prev, `gap-queued-${i}`]);
                                      setManuscriptAnalysis(prev => ({
                                        ...prev,
                                        gaps: prev.gaps.map((gap, idx) => idx === i ? `⏳ QUEUED: ${gap}` : gap)
                                      }));
                                      toast.success(res.data.message || "Fix queued! Will apply when outlines are created.");
                                    } else {
                                      setFixedIssues(prev => [...prev, `gap-${i}`]);
                                      setManuscriptAnalysis(prev => ({
                                        ...prev,
                                        gaps: prev.gaps.map((gap, idx) => idx === i ? `✓ FIXED: ${gap}` : gap)
                                      }));
                                      toast.success(res.data.message || "Gap fixed! New content added.");
                                    }
                                    playComplete();
                                  } else {
                                    toast.error(res.data.message || "Failed to fix gap");
                                  }
                                } catch (err) {
                                  toast.error(err?.response?.data?.detail || "Failed to fix issue");
                                } finally {
                                  setFixingIssue(null);
                                }
                              }}
                              data-testid={`fix-gap-${i}`}
                            >
                              {fixingIssue?.type === 'gap' && fixingIssue?.index === i ? (
                                <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> {manuscriptOutlines ? 'Fixing...' : 'Queuing...'}</>
                              ) : (
                                <><Wand2 className="w-3 h-3 mr-1" /> {manuscriptOutlines ? 'Fix This' : 'Queue Fix'}</>
                              )}
                            </Button>
                          )}
                        </div>
                      );
                    })}
                    {!manuscriptOutlines && manuscriptAnalysis.gaps.some(g => !g.startsWith('✓ FIXED:')) && (
                      <p className="text-[10px] text-cyan-400 mt-2 italic">
                        Tip: Fix buttons will queue changes to apply when outlines are generated
                      </p>
                    )}
                  </div>
                )}
                
                {/* Strengths */}
                {manuscriptAnalysis.strengths?.length > 0 && (
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
                    <h4 className="text-sm font-semibold text-emerald-400 mb-2 flex items-center gap-2">
                      <Star className="w-4 h-4" />
                      Strengths
                    </h4>
                    {manuscriptAnalysis.strengths.map((s, i) => (
                      <p key={i} className="text-xs text-muted-foreground mb-1">• {s}</p>
                    ))}
                  </div>
                )}
                
                <div className="flex items-center gap-2 pt-2">
                  <span className="text-xs text-muted-foreground">Status:</span>
                  <Badge className={manuscriptAnalysis.is_finished ? "bg-emerald-600" : "bg-cyan-600"}>
                    {manuscriptAnalysis.is_finished ? "Complete Manuscript" : "Work in Progress"}
                  </Badge>
                </div>
                
                {/* Action buttons */}
                <div className="grid grid-cols-2 gap-2 pt-2">
                  <Button
                    className="bg-cyan-600 hover:bg-cyan-500"
                    onClick={async () => {
                      setUploadStep("analyzing");
                      setAnalyzingFile(true);
                      try {
                        const result = await pollManuscriptOutlines(manuscriptId, userId);
                        setManuscriptOutlines(result.outlines);
                        setUploadStep("outlines");
                        toast.success(`${result.chapter_count} outlines created!`);
                      } catch (err) {
                        toast.error("Outline generation failed");
                        setUploadStep("analysis_done");
                      } finally {
                        setAnalyzingFile(false);
                      }
                    }}
                    data-testid="create-outlines-btn"
                  >
                    <BookOpen className="w-4 h-4 mr-2" />
                    Create Outlines
                  </Button>
                  <Button
                    variant="outline"
                    className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                    onClick={async () => {
                      setUploadStep("analyzing");
                      setAnalyzingFile(true);
                      try {
                        const logEntry = (msg) => {
                        // Ensure msg is a simple string to avoid serialization issues
                        const safeMsg = typeof msg === 'string' ? msg : String(msg);
                        setUploadActivityLog(prev => [...prev, { time: new Date().toLocaleTimeString(), msg: safeMsg }]);
                      };
                        logEntry("Running AI diagnosis...");
                        const dRes = await axios.post(`${API}/manuscript/diagnose/${manuscriptId}`, {}, { timeout: 120000 });
                        setManuscriptDiagnosis(dRes.data.diagnosis);
                        logEntry(`Diagnosis complete: Score ${dRes.data.diagnosis?.overall_score}/100`);
                        setUploadStep("diagnose");
                      } catch (err) {
                        toast.error("Diagnosis failed");
                        setUploadStep("analysis_done");
                      } finally {
                        setAnalyzingFile(false);
                      }
                    }}
                    data-testid="diagnose-btn"
                  >
                    <Stethoscope className="w-4 h-4 mr-2" />
                    Is It Good?
                  </Button>
                </div>

                {/* Activity Log */}
                {uploadActivityLog.length > 0 && (
                  <div className="bg-slate-900/80 border border-slate-700 rounded-lg p-3 max-h-32 overflow-y-auto" data-testid="analysis-activity-log">
                    <p className="text-xs font-mono text-slate-400 mb-1">Activity Log</p>
                    {uploadActivityLog.map((entry, i) => (
                      <p key={i} className={`text-xs font-mono ${entry.msg.startsWith('ERROR') ? 'text-red-400' : 'text-slate-400'}`}>
                        <span className="text-slate-500">[{entry.time}]</span> {entry.msg}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Outlines View */}
            {uploadStep === "outlines" && manuscriptOutlines && (
              <div className="space-y-4">
                <div className="text-center mb-2">
                  <h3 className="font-semibold text-lg text-foreground">Chapter Outlines</h3>
                  <p className="text-sm text-muted-foreground">{manuscriptOutlines.length} chapters ready</p>
                </div>
                
                <div className="max-h-60 overflow-y-auto space-y-2 pr-1">
                  {manuscriptOutlines.map((ch, i) => (
                    <div key={i} className="bg-card border border-border rounded-lg p-3" data-testid={`outline-ch-${i}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-sm text-foreground">Ch. {ch.number || i + 1}: {ch.title}</span>
                        {ch.pov && <span className="text-xs text-muted-foreground">POV: {ch.pov}</span>}
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">{ch.summary}</p>
                      {ch.key_beats?.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {ch.key_beats.slice(0, 3).map((beat, j) => (
                            <span key={j} className="text-[10px] bg-cyan-500/15 text-cyan-400 px-2 py-0.5 rounded-full">{beat.length > 40 ? beat.substring(0, 40) + '...' : beat}</span>
                          ))}
                          {ch.key_beats.length > 3 && <span className="text-[10px] text-muted-foreground">+{ch.key_beats.length - 3} more</span>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* World-building toggle */}
                <div className="bg-card border border-border rounded-lg p-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input 
                      type="checkbox" 
                      className="w-4 h-4 rounded border-zinc-600 text-cyan-400 focus:ring-cyan-500"
                      onChange={(e) => {
                        if (e.target.checked) {
                          // Switch to manual mode with pre-filled data
                          setFormData(prev => ({
                            ...prev,
                            title: manuscriptAnalysis?.title || "",
                            genre: manuscriptAnalysis?.genre || "",
                            tone: manuscriptAnalysis?.tone || "",
                            initial_premise: manuscriptAnalysis?.summary || "",
                            num_chapters: manuscriptOutlines?.length || 30
                          }));
                          toast("World-building form will appear after creating the project", { icon: "i" });
                        }
                      }}
                    />
                    <div>
                      <span className="text-sm font-medium text-foreground">Add world-building details</span>
                      <p className="text-xs text-muted-foreground">Optional — customize genre, tone, and world settings before generating</p>
                    </div>
                  </label>
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    className="bg-cyan-600 hover:bg-cyan-500"
                    disabled={loading}
                    onClick={async () => {
                      setLoading(true);
                      try {
                        const res = await axios.post(
                          `${API}/manuscript/create-project/${manuscriptId}?user_id=${userId}`,
                          {},
                          { timeout: 60000 }
                        );
                        toast.success("Project created! Opening your book...");
                        onCreated && onCreated(res.data);
                        navigate(`/write/${res.data.id}`);
                        onOpenChange(false);
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Failed to create project");
                      } finally {
                        setLoading(false);
                      }
                    }}
                    data-testid="create-project-from-outlines"
                  >
                    {loading ? "Creating..." : "Create Project & Write"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setUploadStep("analysis_done")}
                  >
                    Back to Analysis
                  </Button>
                </div>
              </div>
            )}

            {/* Diagnosis View */}
            {uploadStep === "diagnose" && manuscriptDiagnosis && (
              <div className="space-y-4">
                <div className="text-center mb-2">
                  <h3 className="font-semibold text-lg text-foreground">Story Diagnosis</h3>
                  <div className="flex items-center justify-center gap-2 mt-1">
                    <div className={`text-3xl font-bold ${
                      (manuscriptDiagnosis.overall_score || 0) >= 80 ? 'text-emerald-400' :
                      (manuscriptDiagnosis.overall_score || 0) >= 60 ? 'text-cyan-400' : 'text-red-400'
                    }`}>
                      {manuscriptDiagnosis.overall_score || 0}/100
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{manuscriptDiagnosis.verdict}</p>
                </div>
                
                {/* Strengths */}
                {manuscriptDiagnosis.strengths?.length > 0 && (
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
                    <h4 className="text-sm font-semibold text-emerald-400 mb-2 flex items-center gap-2">
                      <Star className="w-4 h-4" />
                      Strengths
                    </h4>
                    {manuscriptDiagnosis.strengths.map((s, i) => (
                      <div key={i} className="mb-2">
                        <p className="text-xs font-medium text-foreground">{s.title}</p>
                        <p className="text-xs text-muted-foreground">{s.description}</p>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Weaknesses with Fix button */}
                {manuscriptDiagnosis.weaknesses?.length > 0 && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                    <h4 className="text-sm font-semibold text-red-400 mb-2 flex items-center gap-2">
                      <Target className="w-4 h-4" />
                      Areas to Improve
                    </h4>
                    {manuscriptDiagnosis.weaknesses.map((w, i) => {
                      const isFixed = fixedIssues.includes(`weakness-${i}`);
                      return (
                        <div key={i} className={`mb-3 p-2 rounded ${isFixed ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-slate-800/50'}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <p className={`text-xs font-medium ${isFixed ? 'text-emerald-400' : 'text-foreground'}`}>
                                {isFixed ? '✓ ' : ''}{w.title}
                              </p>
                              <p className={`text-xs ${isFixed ? 'text-emerald-400/70' : 'text-muted-foreground'}`}>{w.description}</p>
                            </div>
                            {!isFixed && manuscriptOutlines && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 px-2 text-xs bg-red-600/20 hover:bg-red-600/40 text-red-400 shrink-0"
                                disabled={fixingIssue !== null}
                                onClick={async () => {
                                  setFixingIssue({ type: 'weakness', index: i, description: `${w.title}: ${w.description}` });
                                  try {
                                    const res = await axios.post(`${API}/manuscript/fix-issue/${manuscriptId}`, {
                                      issue_type: 'weakness',
                                      issue_description: `${w.title}: ${w.description}`,
                                      issue_index: i,
                                      chapters_to_merge: []
                                    }, { timeout: 120000 });
                                    if (res.data.success) {
                                      setFixedIssues(prev => [...prev, `weakness-${i}`]);
                                      toast.success(res.data.message || "Weakness addressed!");
                                      playComplete();
                                    } else {
                                      toast.error(res.data.message || "Failed to fix weakness");
                                    }
                                  } catch (err) {
                                    toast.error(err?.response?.data?.detail || "Failed to fix issue");
                                  } finally {
                                    setFixingIssue(null);
                                  }
                                }}
                                data-testid={`fix-weakness-${i}`}
                              >
                                {fixingIssue?.type === 'weakness' && fixingIssue?.index === i ? (
                                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Fixing...</>
                                ) : (
                                  <><Wand2 className="w-3 h-3 mr-1" /> Fix This</>
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {!manuscriptOutlines && (
                      <p className="text-[10px] text-cyan-400 mt-2 italic">
                        Tip: Fix buttons will queue changes to apply when outlines are generated
                      </p>
                    )}
                  </div>
                )}
                
                {/* Improvement Ideas — Apply Each */}
                {manuscriptDiagnosis.improvement_ideas?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-foreground">Improvement Ideas</h4>
                    {manuscriptDiagnosis.improvement_ideas.map((idea, i) => (
                      <div 
                        key={i} 
                        className={`p-3 rounded-lg border transition-all ${
                          appliedIdeas.includes(idea.id)
                            ? 'border-emerald-500/50 bg-emerald-500/10' 
                            : 'border-border bg-card'
                        }`}
                        data-testid={`improvement-${idea.id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium text-foreground">{idea.title}</span>
                              <Badge className={`text-[10px] ${
                                idea.impact === 'high' ? 'bg-red-600' : idea.impact === 'medium' ? 'bg-cyan-600' : 'bg-zinc-600'
                              }`}>{idea.impact}</Badge>
                              {idea.chapters_affected?.length > 0 && (
                                <span className="text-[10px] text-muted-foreground">Ch {idea.chapters_affected.join(', ')}</span>
                              )}
                              {appliedIdeas.includes(idea.id) && (
                                <Badge className="bg-emerald-600 text-[10px]">Applied</Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">{idea.description}</p>
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <input 
                              type="checkbox"
                              checked={selectedImprovements.includes(idea.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedImprovements(prev => [...prev, idea.id]);
                                } else {
                                  setSelectedImprovements(prev => prev.filter(id => id !== idea.id));
                                }
                              }}
                              className="w-4 h-4 mt-0.5 rounded border-zinc-600 text-purple-600 focus:ring-purple-500"
                              title="Select for batch apply"
                            />
                          </div>
                        </div>
                        
                        {/* Preview/Apply buttons */}
                        {!appliedIdeas.includes(idea.id) && previewingIdea !== idea.id && applyingIdea !== idea.id && (
                          <div className="flex gap-2 mt-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs h-7 border-cyan-600/50 text-cyan-400 hover:bg-cyan-600/20"
                              disabled={!!applyingIdea || !!previewingIdea}
                              onClick={async () => {
                                setPreviewingIdea(idea.id);
                                setPreviewChanges(null);
                                const logEntry = (msg) => {
                        // Ensure msg is a simple string to avoid serialization issues
                        const safeMsg = typeof msg === 'string' ? msg : String(msg);
                        setUploadActivityLog(prev => [...prev, { time: new Date().toLocaleTimeString(), msg: safeMsg }]);
                      };
                                logEntry(`Previewing: "${idea.title}"...`);
                                try {
                                  const res = await axios.post(`${API}/manuscript/preview-suggestion-for/${manuscriptId}`, {
                                    suggestion_id: idea.id,
                                    suggestion_title: idea.title,
                                    suggestion_description: idea.description,
                                    suggestion_type: idea.type || 'plot',
                                    chapters_affected: idea.chapters_affected || []
                                  }, { timeout: 120000 });
                                  setPreviewChanges({ ideaId: idea.id, ...res.data });
                                  if (res.data.pending) {
                                    logEntry(`Preview (pending): Will affect ${res.data.total_chapters_affected} chapters when outlines are created`);
                                  } else {
                                    logEntry(`Preview ready: ${res.data.total_chapters_affected} chapters affected`);
                                  }
                                } catch (err) {
                                  logEntry(`ERROR: ${err?.response?.data?.detail || err?.message}`);
                                  toast.error(err?.response?.data?.detail || "Failed to generate preview");
                                } finally {
                                  setPreviewingIdea(null);
                                }
                              }}
                              data-testid={`preview-${idea.id}`}
                            >
                              Preview Changes
                            </Button>
                            <Button
                              size="sm"
                              className="text-xs h-7 bg-purple-600 hover:bg-purple-500"
                              disabled={!!applyingIdea || !!previewingIdea}
                              onClick={async () => {
                                setApplyingIdea(idea.id);
                                const logEntry = (msg) => {
                        // Ensure msg is a simple string to avoid serialization issues
                        const safeMsg = typeof msg === 'string' ? msg : String(msg);
                        setUploadActivityLog(prev => [...prev, { time: new Date().toLocaleTimeString(), msg: safeMsg }]);
                      };
                                logEntry(`Applying: "${idea.title}"...`);
                                try {
                                  const res = await axios.post(`${API}/manuscript/apply-suggestion-for/${manuscriptId}`, {
                                    suggestion_id: idea.id,
                                    suggestion_title: idea.title,
                                    suggestion_description: idea.description,
                                    suggestion_type: idea.type || 'plot',
                                    chapters_affected: idea.chapters_affected || []
                                  }, { timeout: 120000 });
                                  if (res.data.success) {
                                    setAppliedIdeas(prev => [...prev, idea.id]);
                                    // Check if it was queued as pending or applied directly
                                    if (res.data.pending) {
                                      logEntry(`Queued "${idea.title}" — will apply when outlines are created`);
                                      toast.success(`"${idea.title}" queued! It will be applied automatically when you create outlines.`, { duration: 5000 });
                                      playComplete();
                                    } else {
                                      logEntry(`Applied "${idea.title}": ${res.data.changes_applied} changes across ${res.data.details?.length || 0} chapters`);
                                      toast.success(`Applied "${idea.title}" — ${res.data.changes_applied} changes made`);
                                      playComplete();
                                    }
                                  }
                                } catch (err) {
                                  logEntry(`ERROR: ${err?.response?.data?.detail || err?.message}`);
                                  toast.error(err?.response?.data?.detail || "Failed to apply suggestion");
                                } finally {
                                  setApplyingIdea(null);
                                }
                              }}
                              data-testid={`apply-${idea.id}`}
                            >
                              {manuscriptOutlines ? 'Apply This' : 'Queue This'}
                            </Button>
                          </div>
                        )}
                        
                        {/* Loading state */}
                        {(previewingIdea === idea.id || applyingIdea === idea.id) && (
                          <div className="flex items-center gap-2 mt-2 text-xs text-cyan-400">
                            <div className="w-3 h-3 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
                            {previewingIdea === idea.id ? "Generating preview..." : "Applying changes..."}
                          </div>
                        )}
                        
                        {/* Preview results */}
                        {previewChanges?.ideaId === idea.id && (
                          <div className="mt-2 space-y-2 border-t border-slate-700 pt-2">
                            {previewChanges.pending ? (
                              <div className="bg-cyan-500/10 border border-cyan-500/20 rounded p-2">
                                <p className="text-xs font-medium text-cyan-400 flex items-center gap-2">
                                  <span className="w-2 h-2 bg-cyan-400 rounded-full"></span>
                                  Pending Change
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {previewChanges.message || `This improvement will be applied automatically when you create outlines.`}
                                </p>
                                {previewChanges.changes?.length > 0 && (
                                  <p className="text-xs text-cyan-300 mt-1">
                                    Expected to affect ~{previewChanges.total_chapters_affected} chapter(s)
                                  </p>
                                )}
                              </div>
                            ) : (
                              <>
                                <p className="text-xs font-medium text-cyan-400">Preview: {previewChanges.total_chapters_affected} chapter(s) affected</p>
                                {previewChanges.changes?.map((change, ci) => (
                                  <div key={ci} className="bg-slate-900/60 rounded p-2 text-xs">
                                    <p className="font-medium text-foreground">Ch {change.chapter_number}: {change.chapter_title}</p>
                                    {change.mode === "content_edit" ? (
                                      change.edits?.map((edit, ei) => (
                                        <div key={ei} className="mt-1 space-y-1">
                                          {edit.before && (
                                            <div className="bg-red-500/10 border border-red-500/20 rounded p-1.5">
                                              <span className="text-red-400 font-mono text-[10px]">- </span>
                                              <span className="text-red-300 text-[10px]">{edit.before.slice(0, 150)}{edit.before.length > 150 ? '...' : ''}</span>
                                            </div>
                                          )}
                                          {edit.after && (
                                            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded p-1.5">
                                              <span className="text-emerald-400 font-mono text-[10px]">+ </span>
                                              <span className="text-emerald-300 text-[10px]">{edit.after.slice(0, 150)}{edit.after.length > 150 ? '...' : ''}</span>
                                            </div>
                                          )}
                                          <p className="text-slate-400 text-[10px] italic">{edit.reason}</p>
                                        </div>
                                      ))
                                    ) : (
                                      <div className="mt-1 space-y-1">
                                        {change.original_summary && (
                                          <div className="bg-red-500/10 border border-red-500/20 rounded p-1.5">
                                            <span className="text-red-400 font-mono text-[10px]">Before: </span>
                                            <span className="text-red-300 text-[10px]">{change.original_summary.slice(0, 150)}</span>
                                          </div>
                                        )}
                                        {change.new_summary && (
                                          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded p-1.5">
                                            <span className="text-emerald-400 font-mono text-[10px]">After: </span>
                                            <span className="text-emerald-300 text-[10px]">{change.new_summary.slice(0, 150)}</span>
                                          </div>
                                        )}
                                        <p className="text-slate-400 text-[10px] italic">{change.reason}</p>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </>
                            )}
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                className={`text-xs h-7 ${previewChanges.pending ? 'bg-cyan-600 hover:bg-cyan-500' : 'bg-emerald-600 hover:bg-emerald-500'}`}
                                disabled={!!applyingIdea}
                                onClick={async () => {
                                  setApplyingIdea(idea.id);
                                  const logEntry = (msg) => {
                        // Ensure msg is a simple string to avoid serialization issues
                        const safeMsg = typeof msg === 'string' ? msg : String(msg);
                        setUploadActivityLog(prev => [...prev, { time: new Date().toLocaleTimeString(), msg: safeMsg }]);
                      };
                                  logEntry(`${previewChanges.pending ? 'Queuing' : 'Applying'} previewed changes for "${idea.title}"...`);
                                  try {
                                    const res = await axios.post(`${API}/manuscript/apply-suggestion-for/${manuscriptId}`, {
                                      suggestion_id: idea.id,
                                      suggestion_title: idea.title,
                                      suggestion_description: idea.description,
                                      suggestion_type: idea.type || 'plot',
                                      chapters_affected: idea.chapters_affected || []
                                    }, { timeout: 120000 });
                                    if (res.data.success) {
                                      setAppliedIdeas(prev => [...prev, idea.id]);
                                      setPreviewChanges(null);
                                      if (res.data.pending) {
                                        logEntry(`Queued: Will apply when outlines are created`);
                                        toast.success(`"${idea.title}" queued for outline generation!`);
                                        playComplete();
                                      } else {
                                        logEntry(`Applied: ${res.data.changes_applied} changes`);
                                        toast.success(`Applied "${idea.title}"`);
                                        playComplete();
                                      }
                                    }
                                  } catch (err) {
                                    logEntry(`ERROR: ${err?.response?.data?.detail || err?.message}`);
                                    toast.error(err?.response?.data?.detail || "Failed to apply");
                                  } finally {
                                    setApplyingIdea(null);
                                  }
                                }}
                                data-testid={`confirm-apply-${idea.id}`}
                              >
                                {previewChanges.pending ? 'Queue This Change' : 'Confirm & Apply'}
                              </Button>
                              <Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => setPreviewChanges(null)}>
                                Dismiss
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Chapter Size Selector */}
                <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700">
                  <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                    <BookOpen className="w-4 h-4" />
                    Chapter Length
                  </h4>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      onClick={() => setChapterSize("auto")}
                      className={`p-3 rounded-lg border-2 transition-all text-left ${
                        chapterSize === "auto"
                          ? "border-emerald-500 bg-emerald-500/10"
                          : "border-slate-600 hover:border-slate-500"
                      }`}
                      data-testid="chapter-size-auto"
                    >
                      <p className="text-sm font-medium text-foreground">Auto ✨</p>
                      <p className="text-xs text-muted-foreground">Fits each chapter to its outline</p>
                      <p className="text-[10px] text-emerald-400 mt-1">No padding, no repetition</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setChapterSize("medium")}
                      className={`p-3 rounded-lg border-2 transition-all text-left ${
                        chapterSize === "medium" 
                          ? "border-cyan-500 bg-cyan-500/10" 
                          : "border-slate-600 hover:border-slate-500"
                      }`}
                      data-testid="chapter-size-medium"
                    >
                      <p className="text-sm font-medium text-foreground">Medium</p>
                      <p className="text-xs text-muted-foreground">800 - 1,000 words/chapter</p>
                      <p className="text-[10px] text-cyan-400 mt-1">Faster pacing, quick reads</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setChapterSize("large")}
                      className={`p-3 rounded-lg border-2 transition-all text-left ${
                        chapterSize === "large" 
                          ? "border-purple-500 bg-purple-500/10" 
                          : "border-slate-600 hover:border-slate-500"
                      }`}
                      data-testid="chapter-size-large"
                    >
                      <p className="text-sm font-medium text-foreground">Large</p>
                      <p className="text-xs text-muted-foreground">1,800 - 2,100 words/chapter</p>
                      <p className="text-[10px] text-purple-400 mt-1">Rich detail, immersive</p>
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-2 pt-2">
                  <Button
                    className="bg-purple-600 hover:bg-purple-500"
                    disabled={loading}
                    onClick={async () => {
                      setUploadStep("analyzing");
                      setAnalyzingFile(true);
                      try {
                        // Generate outlines first if not done
                        if (!manuscriptOutlines) {
                          const result = await pollManuscriptOutlines(manuscriptId, userId);
                          setManuscriptOutlines(result.outlines);
                        }
                        // Create project with selected improvements and chapter size
                        const idList = selectedImprovements.join(",");
                        const res = await axios.post(
                          `${API}/manuscript/create-project/${manuscriptId}?user_id=${userId}&selected_ideas=${idList}&chapter_size=${chapterSize}`,
                          {},
                          { timeout: 60000 }
                        );
                        toast.success("Project created with your chosen improvements!");
                        onCreated && onCreated(res.data);
                        navigate(`/write/${res.data.id}`);
                        onOpenChange(false);
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Failed to create project");
                        setUploadStep("diagnose");
                      } finally {
                        setAnalyzingFile(false);
                        setLoading(false);
                      }
                    }}
                    data-testid="create-with-improvements"
                  >
                    {selectedImprovements.length > 0 
                      ? `Apply ${selectedImprovements.length} Ideas & Create`
                      : "Create Project"}
                  </Button>
                  <Button variant="outline" onClick={() => setUploadStep("analysis_done")}>
                    Back to Analysis
                  </Button>
                </div>
              </div>
            )}
          </div>
  );
};

export default UploadMode;
